import React, { useState, useRef } from "react";
import { HelixIcon } from "@helix/helix-icon";
import { circle_checkmark as CircleCheckMarkIcon } from "@helix/helix-icon/solid";
import { fetchAccountOptions } from "../../api/session";
import { getCSRFParam, getCSRFToken } from "../../utils/page";
import AccountPicker from "./AccountPicker";
import { buildForgotPasswordUrl } from "./urls";
import {
  CasSsoIcon,
  CloudCmaLogo,
  ErrorIcon,
  LwSsoIcon
} from "./icons";
import {
  CasSso,
  Content,
  Divider,
  Flash,
  Form,
  FormGroup,
  Logo,
  LwSso,
  Reset,
  SubmitButton,
  SubmitContainer,
  Title
} from "./styled/login";
import Input from "../common/Input";
import Label from "../common/Label";

const CSRF_PARAM = getCSRFParam();
const CSRF_TOKEN = getCSRFToken();

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEP = {
  EMAIL: "email",
  SELECT_ACCOUNT: "selectAccount",
  PASSWORD: "password"
};

function Login({
  action = "/users/sign_in",
  lwSsoUrl = "#",
  casSsoUrl = "#",
  forgotPasswordUrl = "/users/password/new",
  flash = {},
  email: initialEmail = "",
  accountId: initialAccountId = null
}) {
  const [step, setStep] = useState(initialEmail && initialAccountId ? STEP.PASSWORD : STEP.EMAIL);
  const [email, setEmail] = useState(initialEmail || "");
  const [password, setPassword] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(initialAccountId || null);
  const [selectedAccountName, setSelectedAccountName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(() => {
    const [entry] = Object.entries(flash || {});
    if (!entry) return null;
    const [key, text] = entry;
    return { text, isError: key === "alert" || key === "error" };
  });

  const requestIdRef = useRef(0);
  const formRef = useRef(null);
  const emailInputRef = useRef(null);

  function showError(text) {
    setMessage({ text, isError: true });
  }

  function clearMessage() {
    setMessage(null);
  }

  async function handleContinue() {
    if (!EMAIL_RE.test(email)) {
      showError("Please enter a valid email address.");
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    clearMessage();

    try {
      const results = await fetchAccountOptions(email);

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (!results.length) {
        showError("We couldn't find a Cloud CMA account for that email.");
        return;
      }

      setAccounts(results);
      if (results.length === 1) {
        const [account] = results;

        if (account?.platformSignInRequired) {
          window.location.href = lwSsoUrl;
          return;
        }

        setSelectedAccountId(account.accountId);
        setStep(STEP.PASSWORD);
      } else {
        setStep(STEP.SELECT_ACCOUNT);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      showError(err.message || "An error occurred while looking up your account.");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }

  function handleAccountSelected(account) {
    setSelectedAccountId(account ? account.accountId : null);
    setSelectedAccountName(account ? account.name : null);
    setStep(STEP.PASSWORD);
  }

  async function handleSignIn() {
    if (!password) {
      showError("Please enter your password.");
      return;
    }
    clearMessage();
    setLoading(true);
    formRef.current.submit();
  }

  function handleFormSubmit(event) {
    event.preventDefault();
    if (step === STEP.PASSWORD) {
      handleSignIn();
    } else {
      handleContinue();
    }
  }

  function resetToEmail(event) {
    if (event) event.preventDefault();

    setStep(STEP.EMAIL);
    setAccounts([]);
    setSelectedAccountId(null);
    setSelectedAccountName(null);
    setPassword("");
    clearMessage();
    setTimeout(() => emailInputRef.current?.focus(), 0);
  }

  if (step === STEP.SELECT_ACCOUNT) {
    return (
      <>
        <Content expandContainer>
          <Logo>
            <CloudCmaLogo />
          </Logo>
          <AccountPicker
            lwSsoUrl={lwSsoUrl}
            email={email}
            accounts={accounts}
            onContinue={handleAccountSelected}
          />
        </Content>
        <Reset>
          <a href={action} onClick={resetToEmail}>
            Use a different email
          </a>
        </Reset>
      </>
    );
  }

  const showPassword = step === STEP.PASSWORD;

  return (
    <Content>
      <Logo>
        <CloudCmaLogo />
      </Logo>
      <Form
        ref={formRef}
        action={action}
        method="post"
        onSubmit={handleFormSubmit}>
        <Title>Sign In</Title>
        <input type="hidden" name={CSRF_PARAM} value={CSRF_TOKEN} />
        <input
          type="hidden"
          name="user[account_id]"
          value={selectedAccountId ?? ""}
        />
        <FormGroup>
          <LwSso href={lwSsoUrl}>
            <span className="sso-icon">
              <LwSsoIcon />
            </span>
            <span className="sso-text">Sign in with Lone Wolf Account</span>
          </LwSso>
        </FormGroup>
        <FormGroup>
          <CasSso href={casSsoUrl}>
            <span className="sso-icon">
              <CasSsoIcon />
            </span>
            <span className="sso-text">Sign in with Cloud Agent Suite</span>
          </CasSso>
        </FormGroup>
        <FormGroup>
          <Divider>
            <span>Or</span>
          </Divider>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="user_email">Email</Label>
          <Input
            ref={emailInputRef}
            id="user_email"
            type="email"
            name="user[email]"
            placeholder="john@acmerealty.com"
            autoComplete="email"
            value={email}
            readOnly={showPassword}
            onChange={(event) => {
              setEmail(event.target.value);
              clearMessage();
            }}
          />
        </FormGroup>
        {showPassword && selectedAccountName && (
          <FormGroup>
            <Flash $center role="status">
              <HelixIcon icon={CircleCheckMarkIcon} />
              Signing in with{" "}{selectedAccountName}
            </Flash>
          </FormGroup>
        )}
        {showPassword && (
          <FormGroup>
            <Label htmlFor="user_password">Password</Label>
            <Input
              id="user_password"
              type="password"
              name="user[password]"
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                clearMessage();
              }}
            />
          </FormGroup>
        )}
        <SubmitContainer>
          {showPassword ? (
            <SubmitButton
              type="submit"
              name="commit"
              value="Sign In"
              disabled={loading}>
              Sign In
            </SubmitButton>
          ) : (
            <SubmitButton type="submit" disabled={loading}>
              Continue
            </SubmitButton>
          )}
        </SubmitContainer>
        {message && (
          <FormGroup $top>
            <Flash $error={message.isError} role="alert">
              {message.isError ? (
                <ErrorIcon />
              ) : (
                <HelixIcon icon={CircleCheckMarkIcon} />
              )}
              {message.text}
            </Flash>
          </FormGroup>
        )}
        {showPassword && (
        <Reset>
          <a
            href={buildForgotPasswordUrl(
              forgotPasswordUrl,
              email,
              selectedAccountId
            )}>
            Forgot Password?
          </a>
        </Reset>
        )}
      </Form>
       <Reset>
          {showPassword && (
            <a href={action} onClick={resetToEmail}>
              Use a different email
            </a>
          )}
       </Reset>
    </Content>
  );
}

export default Login;
